import { Auth } from 'aws-amplify';

import { kind as addTrackingEvent } from '../../actions/AddTrackingEvent';
import { wipeComplete } from '../../actions/completions/WipeComplete';
import { logError } from '../../logError';
import { Task } from '../../Task';
import { TrackingEventType } from '../../TrackingEvent';

// Workbox-managed IndexedDB stores that should not be deleted during logout.
// Deleting these while the service worker holds them open causes 'blocked' timeouts.
// The service worker will recreate them as needed.
const WORKBOX_DB_PREFIXES = ['workbox-expiration', 'workbox-background-sync', 'workbox-precache'];

export function isWorkboxDatabase(name: string): boolean {
  return WORKBOX_DB_PREFIXES.some(prefix => name.startsWith(prefix));
}

export async function deleteIndexedDBDatabase(iDb: IDBDatabaseInfo) {
  return new Promise((resolve, reject) => {
    if (!iDb.name) {
      reject('Missing IndexedDB database name');
      return;
    }

    // Skip Workbox-internal databases to avoid blocking the service worker's open connections
    if (isWorkboxDatabase(iDb.name)) {
      resolve(true);
      return;
    }

    const request = window.indexedDB.deleteDatabase(iDb.name);

    setTimeout(() => {
      try {
        // Trying to access error on Safari throws error if in use.
        // We need a way to trigger `request.error`;
        // `console.debug` accomplishes this plus gives us debugging information.
        // eslint-disable-next-line no-console
        console.debug('Checking for database deletion:', iDb.name, request.error);
      } catch (e) {
        resolve(true); // need to resolve, otherwise will skip other dbs
      }
    }, 5000);

    request.onsuccess = resolve;
    request.onerror = reject;
    request.onblocked = resolve;
  });
}

export function wipe(): Task {
  return {
    kind: 'Wipe',
    start: async dispatch => {
      // Formerly `signOutUser` and `clearCacheStorageAndLogout` in `src/rxdb/replication-v2/App/Logout.tsx`

      const iDbList = await window.indexedDB.databases();

      for (const iDb of iDbList) {
        try {
          await deleteIndexedDBDatabase(iDb);
        } catch (error) {
          dispatch({
            kind: addTrackingEvent,
            trackingEvent: {
              type: TrackingEventType.ERROR_REMOVING_DATABASE_DURING_LOGOUT,
              blob: {
                error,
                indexDBDatabaseName: iDb?.name,
              },
            },
          });
        }
      }

      // Revokes all existing refresh tokens
      const globalLogout = localStorage.getItem('globalLogout') === 'true';
      try {
        await Auth.signOut({
          global: globalLogout,
        });
      } catch (error) {
        logError('Error signing out from Auth', error, {
          globalLogout,
        });
      }

      if ('serviceWorker' in navigator) {
        const cache = await caches.open(`s3-images`);
        const imageKeys = await cache.keys();
        await Promise.all(imageKeys.map(key => cache.delete(key)));
      }

      sessionStorage.clear();
      localStorage.clear();

      dispatch(wipeComplete);
    },
  };
}
